// Loads the classic (non-module) Cesium.js and mars3d.js scripts on demand.
// These are ~3.1MB combined and were previously hardcoded as unconditional
// <script> tags in index.html, so every page paid for them even when the
// map was never shown. mars3d's UMD wrapper hard-depends on window.Cesium
// already existing, so Cesium must load and execute before mars3d.
// This file must never import 'mars3d' or 'cesium' itself, or it would
// pull the heavy bundles back into whatever chunk imports it.

let runtimePromise: Promise<void> | null = null

function versionedRuntimeAsset(path: string, version: string): string {
  return `${path}?v=${encodeURIComponent(version)}`
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

function loadStylesheet(href: string): void {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.type = 'text/css'
  link.href = href
  document.head.appendChild(link)
}

export function loadMars3dRuntime(): Promise<void> {
  if (!runtimePromise) {
    loadStylesheet(
      versionedRuntimeAsset('/lib/mars3d-cesium/Widgets/widgets.css', __CESIUM_RUNTIME_VERSION__),
    )
    loadStylesheet(versionedRuntimeAsset('/lib/mars3d/mars3d.css', __MARS3D_RUNTIME_VERSION__))
    runtimePromise = loadScript(
      versionedRuntimeAsset('/lib/mars3d-cesium/Cesium.js', __CESIUM_RUNTIME_VERSION__),
    ).then(() => {
      if (
        window.Cesium.VERSION !== __CESIUM_RUNTIME_VERSION__ ||
        typeof window.Cesium.MVTDataProvider !== 'function'
      ) {
        throw new Error(
          `Cesium ${__CESIUM_RUNTIME_VERSION__} with MVTDataProvider is required; loaded ${window.Cesium.VERSION}`,
        )
      }
      return loadScript(
        versionedRuntimeAsset('/lib/mars3d/mars3d.js', __MARS3D_RUNTIME_VERSION__),
      ).then(() => {
        if (window.mars3d.version !== __MARS3D_RUNTIME_VERSION__) {
          throw new Error(
            `Mars3D ${__MARS3D_RUNTIME_VERSION__} is required; loaded ${window.mars3d.version}`,
          )
        }
      })
    })
  }
  return runtimePromise
}
